import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Bookmark, Trash2, WifiOff } from "lucide-react";
import { Footer } from "@/components/site/Footer";
import { getSaved, removeSaved, isOfflineAvailable, useLibraryVersion } from "@/lib/library";

function mediaUrl(src) {
  if (!src) return "";
  return src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src;
}

const TopBar = () => (
  <header className="fixed top-0 left-0 right-0 z-[1000] bg-black/70 backdrop-blur-xl border-b border-white/10">
    <div className="flex items-center justify-between px-5 md:px-10 h-[68px]">
      <Link to="/" data-testid="saved-logo" className="font-display text-2xl md:text-3xl tracking-tight leading-none">
        <span className="text-[#E30613]">VL</span><span className="text-white">MAG</span>
      </Link>
      <Link to="/" data-testid="saved-home" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors">
        <ArrowLeft size={16} /> Back To VLMAG
      </Link>
    </div>
  </header>
);

export default function Saved() {
  useLibraryVersion();
  const saved = getSaved();

  return (
    <div className="bg-black text-white min-h-screen" data-testid="saved-page">
      <TopBar />
      <section className="px-5 md:px-10 pt-[108px] md:pt-[128px] pb-20 md:pb-28">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-4">Your Library</p>
        <h1 className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl md:text-7xl">Saved</h1>
        <p className="text-base text-neutral-400 mt-6 leading-relaxed max-w-xl">
          Features you've bookmarked — saved to this device and readable offline, anytime.
        </p>

        {saved.length === 0 ? (
          <div className="mt-16 border border-white/10 py-20 flex flex-col items-center text-center px-6" data-testid="saved-empty">
            <Bookmark size={30} className="text-neutral-700 mb-5" />
            <p className="text-neutral-400 text-lg font-bold">Nothing saved yet</p>
            <p className="text-neutral-600 text-sm mt-2 max-w-sm">Tap the bookmark on any feature to keep it here for later — even with no signal.</p>
            <Link to="/#stories" className="mt-8 border border-[#E30613] px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E30613] transition-colors">
              Browse Stories
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-12" data-testid="saved-grid">
            {saved.map((s, i) => (
              <motion.article
                key={s.id}
                data-testid={`saved-${s.id}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                className="group relative border border-white/10 hover:border-[#E30613] transition-colors duration-500"
              >
                <Link to={`/story/${s.id}`} className="block">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={mediaUrl(s.image)} alt={s.title} className="w-full h-full object-cover grayscale-img group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-0 left-0 bg-black px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#E30613] border-r border-b border-white/10">{s.network}</span>
                    {isOfflineAvailable(s.id) && (
                      <span className="absolute bottom-0 left-0 inline-flex items-center gap-1.5 bg-black/85 backdrop-blur px-2.5 py-1.5 text-[9px] uppercase tracking-[0.15em] font-bold text-emerald-400 border-r border-t border-white/10" data-testid={`offline-badge-${s.id}`}>
                        <WifiOff size={11} /> Offline Ready
                      </span>
                    )}
                  </div>
                  <div className="p-5 md:p-6">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">{s.category}</span>
                    <h3 className="font-display uppercase text-xl md:text-2xl leading-[0.95] tracking-tight text-white group-hover:text-[#E30613] transition-colors duration-300 mt-3">{s.title}</h3>
                    {s.excerpt && <p className="text-sm text-neutral-400 mt-3 leading-relaxed line-clamp-2">{s.excerpt}</p>}
                  </div>
                </Link>
                <button
                  onClick={() => removeSaved(s.id)}
                  data-testid={`saved-remove-${s.id}`}
                  aria-label="Remove"
                  className="absolute top-0 right-0 bg-black/85 backdrop-blur p-2.5 text-neutral-400 hover:text-[#E30613] border-l border-b border-white/10 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </motion.article>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
